/**
 * Bug-board error enrichment helpers.
 *
 * Three small utilities used by both the client error reporter and the
 * server-side recorder to produce more diagnosable bug board entries:
 *
 *   1. classifyStackFrames — tags each stack frame as `app` | `framework` | `vendor`
 *      so the Bug Board UI can collapse vendor frames and surface the line
 *      of YOUR code that caused the error first.
 *
 *   2. detectBotUserAgent — recognizes known crawler / scraper / monitoring
 *      user agents so we can tag (or filter) errors that aren't real customer
 *      issues.
 *
 *   3. (Breadcrumbs are managed in lib/breadcrumbs.ts — separate file because
 *      the buffer state lives there.)
 */

export type FrameKind = 'app' | 'framework' | 'vendor' | 'unknown';

export interface ClassifiedFrame {
  raw: string;
  kind: FrameKind;
  /** Best-guess source location, e.g. "components/Navbar.tsx:147". */
  location?: string;
}

const FRAMEWORK_PATTERNS = [
  /\/_next\//,                 // Next.js build output
  /\bnext\b.*\/dist\//,         // Next.js source (when sourcemapped)
  /\breact-dom\b/,
  /\breact\b\/cjs/,
  /\bscheduler\b/,
];

const VENDOR_PATTERNS = [
  /\/node_modules\//,
  /\b__webpack_require__\b/,
  /\bturbopack-runtime\b/,
];

const APP_PATH_HINTS = [
  /\/(?:app|components|lib|context|hooks|pages)\//,
];

/**
 * Tag every line in a stack trace as app/framework/vendor. Order is preserved.
 */
export function classifyStackFrames(stack: string | null | undefined): ClassifiedFrame[] {
  if (!stack) return [];
  return stack
    .split('\n')
    .map((raw) => raw.trim())
    .filter((l) => l.length > 0 && (l.includes('@') || l.startsWith('at ') || l.includes('/')))
    .map((raw): ClassifiedFrame => {
      const isFramework = FRAMEWORK_PATTERNS.some((p) => p.test(raw));
      const isVendor = VENDOR_PATTERNS.some((p) => p.test(raw));
      const looksLikeApp = APP_PATH_HINTS.some((p) => p.test(raw));
      let kind: FrameKind = 'unknown';
      if (looksLikeApp && !isFramework && !isVendor) kind = 'app';
      else if (isFramework) kind = 'framework';
      else if (isVendor) kind = 'vendor';
      // Cheap location extract: grab the last "/path/file.ext:line" we can find.
      const locMatch = raw.match(/(\/?[\w./@-]+\.(?:tsx?|jsx?|mjs|cjs)):(\d+)(?::\d+)?/);
      return {
        raw,
        kind,
        location: locMatch ? `${locMatch[1]}:${locMatch[2]}` : undefined,
      };
    });
}

/**
 * Return the first frame classified as `app` — the most useful pointer for
 * triaging "where did this happen in OUR code".
 */
export function firstAppFrame(stack: string | null | undefined): ClassifiedFrame | null {
  for (const f of classifyStackFrames(stack)) {
    if (f.kind === 'app') return f;
  }
  return null;
}

// ─── Bot detection ──────────────────────────────────────────────────────

/**
 * Patterns matched against User-Agent. Conservative — we only flag clearly
 * non-human UAs. We do NOT match on "Mozilla" alone or other ambiguous tokens.
 */
const BOT_UA_PATTERNS: { pattern: RegExp; label: string }[] = [
  { pattern: /\bGooglebot\b/i,                   label: 'Googlebot' },
  { pattern: /\bbingbot\b/i,                     label: 'Bingbot' },
  { pattern: /\bDuckDuckBot\b/i,                 label: 'DuckDuckGo' },
  { pattern: /\bYandexBot\b/i,                   label: 'YandexBot' },
  { pattern: /\bBaiduspider\b/i,                 label: 'Baidu' },
  { pattern: /\bGPTBot\b/i,                      label: 'OpenAI-GPT' },
  { pattern: /\bChatGPT-User\b/i,                label: 'ChatGPT' },
  { pattern: /\bOAI-SearchBot\b/i,               label: 'OpenAI-Search' },
  { pattern: /\bClaudeBot\b/i,                   label: 'Anthropic-Claude' },
  { pattern: /\banthropic-ai\b/i,                label: 'Anthropic' },
  { pattern: /\bClaude-Web\b/i,                  label: 'Anthropic' },
  { pattern: /\bPerplexityBot\b/i,               label: 'Perplexity' },
  { pattern: /\bPerplexity-User\b/i,             label: 'Perplexity' },
  { pattern: /\bGoogle-Extended\b/i,             label: 'Google-AI' },
  { pattern: /\bApplebot\b/i,                    label: 'Apple' },
  { pattern: /\bAmazonbot\b/i,                   label: 'Amazon' },
  { pattern: /\bMeta-ExternalAgent\b/i,          label: 'Meta' },
  { pattern: /\bMeta-ExternalFetcher\b/i,        label: 'Meta' },
  { pattern: /\bmeta-webindexer\b/i,             label: 'Meta' },
  { pattern: /\bSEBot\b/i,                       label: 'SEBot' },
  { pattern: /\bCCBot\b/i,                       label: 'CommonCrawl' },
  { pattern: /\bDuckAssistBot\b/i,               label: 'DuckAssist' },
  { pattern: /\bAhrefsBot\b/i,                   label: 'Ahrefs' },
  { pattern: /\bSemrushBot\b/i,                  label: 'Semrush' },
  { pattern: /\bMJ12bot\b/i,                     label: 'Majestic' },
  { pattern: /\bDotBot\b/i,                      label: 'OpenSiteExplorer' },
  { pattern: /\bBLEXBot\b/i,                     label: 'WebMeUp' },
  { pattern: /\bUptimeRobot\b/i,                 label: 'UptimeRobot' },
  { pattern: /\bPingdom\b/i,                     label: 'Pingdom' },
  { pattern: /\bStatusCake\b/i,                  label: 'StatusCake' },
  { pattern: /\bcurl\//i,                        label: 'curl' },
  { pattern: /\bwget\//i,                        label: 'wget' },
  { pattern: /\bpython-requests\b/i,             label: 'python-requests' },
  { pattern: /\bgo-http-client\b/i,              label: 'go-http' },
  { pattern: /\bjava\/.+/i,                      label: 'java' },
  { pattern: /\bheadless\b/i,                    label: 'HeadlessChrome' },
  { pattern: /\bPhantomJS\b/i,                   label: 'PhantomJS' },
  { pattern: /\bplaywright\b/i,                  label: 'Playwright' },
];

export interface BotDetectionResult {
  isBot: boolean;
  /** e.g. "OpenAI-GPT", "Googlebot", or undefined if not a bot. */
  label?: string;
}

export function detectBotUserAgent(userAgent: string | null | undefined): BotDetectionResult {
  if (!userAgent) return { isBot: false };
  for (const { pattern, label } of BOT_UA_PATTERNS) {
    if (pattern.test(userAgent)) return { isBot: true, label };
  }
  return { isBot: false };
}

/**
 * Browser-error messages that never come from our code. ErrorReporter drops
 * them before sending, and /api/errors/ingest drops them again so browser
 * tabs still running an older bundle don't keep filing them.
 */
export const CLIENT_NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /Script error\./i,
  // Common third-party noise — extend as needed.
  /chrome-extension:/i,
  /moz-extension:/i,
  /safari-extension:/i,
  // Outlook SafeLinks / Microsoft Defender for Office rewrites URLs in
  // emails and breaks embedded scripts. Signature: "Object Not Found
  // Matching Id:N, MethodName:update, ParamCount:4". Not our bug, pure noise.
  /Object Not Found Matching Id:\d+, MethodName:/i,
  // In-app browsers (Facebook / Instagram / TikTok WKWebView) inject JS that
  // probes the native iOS bridge. When it's absent the injected script throws
  // "undefined is not an object (evaluating 'window.webkit.messageHandlers')".
  // Not our code — pure environment noise from paid-social in-app traffic.
  /window\.webkit/i,
  /messageHandlers/i,
  /WKWebView/i,
  // Microsoft UET / Google Ads tracking tags create a tracking iframe that iOS
  // Safari ITP blocks; the tag then throws "evaluating 'iframe.id'". Third-party.
  /evaluating 'iframe\./i,
  /\biframe\.(id|contentWindow|src)\b/i,
  // iOS Safari password-autofill internal callback — browser internals, not ours.
  /_?AutofillCallback/i,
  /Can't find variable: _Autofill/i,
  // Crypto-wallet shims injected by Brave (iOS) and wallet extensions:
  // "evaluating 'window.ethereum.selectedAddress = undefined'". We have no web3 code.
  /window\.(ethereum|solana)\b/i,
  // Browser-extension messaging (DuckDuckGo, Safari extensions):
  // "Invalid call to runtime.sendMessage(). Tab not found."
  /runtime\.sendMessage/i,
  // A promise rejected with a bare DOM Event: no message and no stack, so
  // there is nothing to act on. ErrorReporter tests the bare message; the
  // ingest endpoint sees it with the "[unhandledrejection] " prefix.
  /^(\[unhandledrejection\] )?\[object Event\]$/,
];

export function isClientNoise(message: string | null | undefined): boolean {
  if (!message) return false;
  return CLIENT_NOISE_PATTERNS.some((p) => p.test(message));
}
